'use client';

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { useShallow } from 'zustand/react/shallow';
import { toast } from '@/shared/lib/toast';
import { UnifiedRenderPanel } from '@/features/design/render';
import { useOptionalDesktopLayout } from '@/features/project';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import type { Angle } from '@/types/concept';
import type { Render } from '@/types/render';
import type {
  Concept,
  ConceptAngle,
  Control,
  Reference,
} from '@/types/concept';
import { useSubscriptionLimits, usePlanTier } from '@/app/model/stores';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import { useJobQueue } from '@/features/design/job-queue';
import { useInitializeRendersFromData } from '@/features/design/render/hooks/use-initialize-renders';
import { useConceptWebSocket } from '../hooks/useConceptWebSocket';
import { useConceptActions } from '../hooks/useConceptActions';
import { MainView } from './MainView';
import { DerivativeAnglesView } from './DerivativeAnglesView';
import { AlbumCollapsiblePanel, AlbumListWithViewer } from '@/features/design/album';
import {
  EstimateTabContent,
  useConceptEstimates,
  useEstimateActions,
  selectCurrentEstimate,
} from '@/features/design/estimate';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import {
  FavoriteButton,
  useConceptFavorite,
  AngleDeleteConflictDialog,
  BackButton,
} from '@/shared/ui';
import { AngleUpload } from '@/features/angle';
import { Button } from '@/shared/ui/shadcn/button';
import { ConceptActions } from './ConceptActions';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import {
  setBottomBarReveal,
  computeBottomBarRevealProgress,
} from '@/shared/lib/bottom-bar-reveal';
import { RenameAngleDialog } from './RenameAngleDialog';
import { anglesService } from '@/shared/api/services/angles.service';
import { conceptsService } from '@/shared/api/services/concepts.service';
import { useProjectAngles } from '@/shared/api/hooks/use-project-angles';
import { isProjectAngleValid, isRenderCandidate } from '@/types/project-angle';
import { ConceptNotesDialog } from './ConceptNotesDialog';
import { ConceptAnglesEditModal } from './ConceptAnglesEditModal';
import { ConceptContentTabs } from './ConceptContentTabs';
import { ConceptAngleType } from '@/shared/constants/concept-angle-type';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';
import type { MiddlePanelTab } from '@/features/project/context/DesktopLayoutContext';
import type { Project } from '@/types/project';
import { FeedbackHeaderButton } from '@/features/support/ui/FeedbackHeaderButton';

interface ConceptEditorProps {
  project: Project;
  concept: Concept;
  conceptAngles: ConceptAngle[];
  angles: Record<string, Angle>;
  renders: Record<string, Render[]>;
  controls: Record<string, Control[]>;
  references: Reference[];
  onRefetch: () => void;
  isArchived?: boolean;
  onDraggingSliderChange?: (isDragging: boolean) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  initialTab?: MiddlePanelTab;
  initialAngleId?: string;
  /// Per-navigation arrival token from the URL (`?n=`). The render-complete
  /// toast stamps it with the completed render id, so every tap is a distinct
  /// arrival. This page does NOT remount when the user re-navigates onto the
  /// concept they're already viewing (same `key={conceptId}`), so the nonce is
  /// the only input that reliably changes — it re-applies the target tab/angle
  /// and re-scrolls even when the tab/angle values match the previous arrival
  /// (e.g. user manually switched to Blueprint, then a new render lands on the
  /// Design tab). Mirrors Apple `arrivalNonce`.
  initialArrivalNonce?: string;
  /// One-shot "freshly created" flag (`?created=1`), set only by the two
  /// concept-creation pushes. Plays a single attention pulse on the header
  /// title so the user notices a new design page was created; the param is
  /// stripped from the URL immediately after firing so refresh / back / paging
  /// never replay it. Parity with Apple/Android.
  justCreated?: boolean;
  /// Persist a tab switch into the URL (`?tab=`). The URL is the single source of
  /// truth for the active tab, so switching rewrites the address (replace) and
  /// `initialTab` flows back in. Supplied by the concept page.
  onTabChange?: (tab: MiddlePanelTab) => void;
}

// Runs before paint on the client (so the bottom-nav reveal can be set to its
// hidden state before the first frame — no visible-then-hide flash on load),
// and falls back to useEffect during SSR where layout effects don't run.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function ConceptEditor({
  project,
  concept: conceptProp,
  conceptAngles: conceptAnglesProp,
  angles: anglesProp,
  renders: rendersProp,
  controls: controlsProp,
  references: referencesProp,
  onRefetch,
  isArchived = false,
  onDraggingSliderChange,
  onSwipeLeft,
  onSwipeRight,
  initialTab,
  initialAngleId,
  initialArrivalNonce,
  justCreated = false,
  onTabChange,
}: ConceptEditorProps) {
  const tConcept = useTranslations('concept');
  const tCommon = useTranslations('common');
  const {
    ui,
    setMobileHeaderData,
    loadSelectedRenderIds,
    setError,
    reset,
  } = useConceptStore(
    useShallow((state) => ({
      ui: state.ui,
      setMobileHeaderData: state.setMobileHeaderData,
      loadSelectedRenderIds: state.loadSelectedRenderIds,
      setError: state.setError,
      reset: state.reset,
    }))
  );

  // Desktop layout context
  const desktopLayout = useOptionalDesktopLayout();
  const { isLg, isXl } = useBreakpoint();
  const showInlineRenderPanel = isXl && desktopLayout;
  // Floating bottom nav (`MobileNavigation`) is visible below lg, and at lg+
  // only when the sidebar is collapsed. The stacked Generate capsule clears it.
  const sidebarCollapsed = useSidebarStore((s) => s.collapsed);
  const navVisible = !isLg || sidebarCollapsed;
  const stackedFooterBottomClassName = navVisible ? 'bottom-[84px]' : 'bottom-6';

  // Scroll-linked bottom-nav reveal — single-column (stacked) concept page only,
  // and only when the floating nav island is present (`navVisible`). Writes a
  // 0..1 progress into a CSS var that the fixed nav + Generate capsule read via
  // transform, so they slide with the scroll finger (no React re-render). At the
  // top the nav is hidden (only the Generate capsule shows); ~half a viewport of
  // scroll reveals it fully. `showInlineRenderPanel` (= isXl && desktopLayout) is
  // the two-column inspector layout, which scrolls internally — excluded.
  const stackedRevealActive = !showInlineRenderPanel && navVisible;
  useIsomorphicLayoutEffect(() => {
    if (!stackedRevealActive) {
      setBottomBarReveal(null);
      return;
    }
    let raf = 0;
    const update = () => {
      raf = 0;
      // End-anchored: stay hidden through the page, reveal only over the last
      // `BOTTOM_BAR_REVEAL_WINDOW_PX` of scroll so the nav is fully shown right
      // at the bottom of the content. While a sub-panel holds the bar hidden
      // this write is suppressed inside setBottomBarReveal.
      setBottomBarReveal(computeBottomBarRevealProgress());
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update(); // start hidden at the top
    // Deep-link guard: this effect runs in a layout effect, BEFORE the shell's
    // `flex-1 min-h-0 / h-full` height chain has bounded the scroll `<main>`.
    // For that first frame the document itself overflows, so the `update()`
    // above reads a huge distance-to-bottom and pins the bar hidden — and since
    // the real scroller at `lg+` is `<main>` (not the window), the window
    // listener never fires to correct it, leaving the nav stranded hidden until
    // a remount (the "leave and come back fixes it" symptom). Recompute once the
    // layout has settled so the progress reflects the bounded document.
    const settleRaf = requestAnimationFrame(() =>
      requestAnimationFrame(update)
    );
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
      cancelAnimationFrame(settleRaf);
      setBottomBarReveal(null); // restore fully-shown nav on leave
    };
    // `conceptProp?.id` is a dep on purpose: paging prev/next routes via
    // `router.push` to the sibling `[conceptId]` segment, which the App Router
    // serves with the SAME page subtree — `ConceptDetailContainer` re-reads
    // params and feeds a new `concept` prop WITHOUT remounting this component
    // (it carries no `key`). Without the id in the deps the effect never re-runs,
    // so `update()` (which starts hidden) isn't re-called and the bar leaks to
    // shown after paging. Keying on the concept id re-hides it on every page.
  }, [stackedRevealActive, conceptProp?.id]);
  const prevConceptId = conceptProp?.meta?.navigation?.prev_concept_id ?? null;
  const nextConceptId = conceptProp?.meta?.navigation?.next_concept_id ?? null;
  const activeTab = desktopLayout?.activeTab ?? 'design';
  const selectedAngleIdFromContext = desktopLayout?.selectedAngleId;

  // Subscription limits
  const { isMax } = usePlanTier();
  const limits = useSubscriptionLimits();

  // Initialize render queue
  useJobQueue(10);

  // Use props
  const concept = conceptProp;
  const conceptAngles = conceptAnglesProp;
  const angles = anglesProp;
  const renders = rendersProp;

  // "Delete estimate" in the page kebab — Estimate tab only, and only when the
  // concept's current estimate is terminal (ready/failed). Pending/generating
  // estimates are excluded: GenerateEstimateJob has no trashed-row guard, so a
  // mid-run delete would still finish (and deduct tokens) against an
  // already-deleted estimate. Same query key as EstimateTabContent — deduped.
  const { data: conceptEstimates } = useConceptEstimates(
    project.id,
    concept?.id ?? ''
  );
  const currentEstimate = selectCurrentEstimate(conceptEstimates);
  const { deleteEstimate, isDeleting: isDeletingEstimate } = useEstimateActions(
    project.id,
    concept?.id ?? ''
  );
  const deletableEstimateId =
    activeTab === 'estimate' &&
    currentEstimate &&
    (currentEstimate.status === 'ready' || currentEstimate.status === 'failed')
      ? currentEstimate.id
      : undefined;
  // "Export estimate" submenu in the page kebab — Estimate tab only, and only
  // when the current estimate is `ready` (export is invalid otherwise).
  const exportableEstimateId =
    activeTab === 'estimate' && currentEstimate?.status === 'ready'
      ? currentEstimate.id
      : undefined;

  // Single source of the favorite toggle (optimistic + debounced). Drives both
  // the standalone header FavoriteButton (two-column) and the kebab favorite
  // item (single-column) so the mutation logic isn't duplicated.
  const { isFavorite: isConceptFavorite, toggle: toggleConceptFavorite } =
    useConceptFavorite({
      projectId: project.id,
      conceptId: concept?.id ?? '',
      isFavorite: concept?.attributes.is_favorite || false,
    });

  // Hydrate the store-side selectedRenderId map from server-saved selections.
  // Concept-level mobile-header fields are no longer mirrored — they're read
  // directly from `useConcept()` in `useConceptHeaderData()` so the header
  // always reflects the current route's cached concept (no setConcept hop).
  useEffect(() => {
    if (concept) {
      loadSelectedRenderIds(conceptAngles);
    }
  }, [concept, conceptAngles, loadSelectedRenderIds]);

  // Refs for scroll targets in single-column layout
  const albumSectionRef = useRef<HTMLDivElement | null>(null);

  // The URL (`?tab=`) is the single source of truth for the active tab. This
  // syncs the layout context FROM the URL whenever it changes: initial mount,
  // reload, deep-link, share, back, paging (carries the tab forward), and the
  // user's own in-page switch (which writes `?tab` via `onTabChange` →
  // `router.replace` → new `initialTab` → here). No arrival-nonce gating needed
  // for the tab anymore — it's purely a function of the address.
  useEffect(() => {
    if (!desktopLayout) return;
    desktopLayout.setActiveTab(initialTab ?? 'design');
  }, [initialTab, desktopLayout]);

  // Apply the target angle (set selected angle in context). `undefined` selects
  // the main angle, a derivative id selects that derivative — so a main-angle
  // render arrival correctly clears any previously-selected derivative. Same
  // per-arrival re-apply as the tab above.
  const appliedAngleNonce = useRef<string | undefined | false>(false);
  useEffect(() => {
    if (!desktopLayout) return;
    if (appliedAngleNonce.current === initialArrivalNonce) return;
    desktopLayout.setSelectedAngleId(initialAngleId);
    appliedAngleNonce.current = initialArrivalNonce;
  }, [initialArrivalNonce, initialAngleId, desktopLayout]);

  // Pending project-angle IDs just attached via upload / ConceptAnglesEditModal.
  // Resolved to a conceptAngle.id by the effect further below once the post-save
  // refetch lands in conceptAngles (the handler's closure still sees pre-refetch
  // data, so the lookup can't run synchronously). Declared here — above the
  // reconcile effect — because that effect reads it to avoid clobbering the
  // just-added selection back to main.
  const [pendingNewAngleIds, setPendingNewAngleIds] = useState<string[]>([]);

  // Reconcile the selected angle with the current concept. Single point so the
  // cases below never race (a separate "clear stale id" effect would clobber the
  // navigation remap in the same commit, both seeing the previous id):
  //  1. Page prev/next (the new concept is the previous one's neighbour): the
  //     persisted selectedAngleId belongs to the PREVIOUS concept. Map its
  //     POSITION onto this concept's ordered angle list (main = 0, derivatives
  //     by order, clamped) so the same view stays selected — users compare the
  //     same angle across concepts. Mirrors Apple's LayoutState index restore.
  //     The DesktopLayoutContext survives this editor's per-concept remount
  //     (key={conceptId}) and carries the previous order + neighbour ids.
  //     Restricting to neighbours (not any jump from a list) matches Apple/
  //     Android, which keep the position only on the chevrons. Explicit deep
  //     links (initialAngleId) win over the remap.
  //  2. Otherwise (arbitrary jump, in-place delete/reorder): a stale selection
  //     id that no longer exists in this concept falls back to the main angle.
  const angleRemapApplied = useRef(false);
  useEffect(() => {
    if (!desktopLayout || !concept || conceptAngles.length === 0) return;

    const orderedSelectionValues = [...conceptAngles]
      .sort((a, b) => {
        const aMain = a.attributes.type === ConceptAngleType.MAIN;
        const bMain = b.attributes.type === ConceptAngleType.MAIN;
        if (aMain && !bMain) return -1;
        if (!aMain && bMain) return 1;
        return a.attributes.order - b.attributes.order;
      })
      .map((ca) =>
        ca.attributes.type === ConceptAngleType.MAIN ? undefined : ca.id
      );

    const memory = desktopLayout.getAngleNavMemory();
    const sel = desktopLayout.selectedAngleId;
    const isStale = sel !== undefined && !orderedSelectionValues.includes(sel);

    // A just-uploaded angle is mid-attach: the pendingNewAngleIds resolver below
    // owns the selection until the new derivative lands in conceptAngles. During
    // that window a refetch can briefly deliver a list without the new id, making
    // the target selection look "stale" — resetting to main here would clobber
    // the auto-switch to the new tab. Defer every stale→main reset to the
    // resolver while a pending selection is outstanding.
    const hasPendingSelection = pendingNewAngleIds.length > 0;

    if (!angleRemapApplied.current) {
      // First reconcile for this freshly mounted concept (= a navigation).
      angleRemapApplied.current = true;
      const isPageNeighbour =
        !!memory &&
        (concept.id === memory.prevId || concept.id === memory.nextId);
      if (!initialAngleId && isPageNeighbour) {
        const oldIndex = Math.max(0, memory.order.indexOf(sel));
        const newIndex = Math.min(oldIndex, orderedSelectionValues.length - 1);
        desktopLayout.setSelectedAngleId(orderedSelectionValues[newIndex]);
      } else if (!initialAngleId && isStale && !hasPendingSelection) {
        // Arbitrary jump carrying a stale selection → main (parity).
        desktopLayout.setSelectedAngleId(undefined);
      }
    } else if (isStale && !hasPendingSelection) {
      desktopLayout.setSelectedAngleId(undefined);
    }

    desktopLayout.rememberAngleNav({
      conceptId: concept.id,
      order: orderedSelectionValues,
      prevId: prevConceptId,
      nextId: nextConceptId,
    });
  }, [
    desktopLayout,
    concept,
    conceptAngles,
    initialAngleId,
    prevConceptId,
    nextConceptId,
    pendingNewAngleIds,
  ]);

  // Scroll to the arrival target in single-column layout after data loads.
  // Re-applied per ARRIVAL (`initialArrivalNonce`), not once-per-mount, so a
  // render-complete toast onto the concept the user is already viewing (no
  // remount) re-scrolls — otherwise the page stays parked wherever it was.
  const appliedScrollNonce = useRef<string | undefined | false>(false);
  useEffect(() => {
    if (showInlineRenderPanel) return; // two-column scrolls internally
    if (!conceptProp) return;
    if (appliedScrollNonce.current === initialArrivalNonce) return;

    // Album tab → scroll the album section into view.
    if (initialTab === 'album') {
      const el = albumSectionRef.current;
      if (!el) return;
      appliedScrollNonce.current = initialArrivalNonce;
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }

    // Main angle on the Design tab: no card/section target, so reset the page
    // to the top to reveal the angle image — a previously scrolled-down page
    // otherwise hides the freshly completed render. The document is the scroll
    // container (the sticky switcher pins at `top-0`). Mirrors the Apple
    // narrow-layout scroll-to-top on toast arrival.
    appliedScrollNonce.current = initialArrivalNonce;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [
    initialArrivalNonce,
    initialTab,
    conceptProp,
    showInlineRenderPanel,
  ]);

  // Initialize Store with full render data
  useInitializeRendersFromData(concept?.id, renders);

  // WebSocket listeners
  useConceptWebSocket({
    projectId: project.id,
    conceptId: concept?.id,
    onRefetch,
  });

  // Action handlers
  const {
    createConceptFromAngle,
    handleAngleUploadSuccess,
    handleDeleteAngle,
    handleDeleteRender,
    handleNoteUpdate,
    deleteConflict,
    deleteConflictDetail,
    isDeletingAngle,
    handleDeleteConflictChoose,
    dismissDeleteConflict,
  } = useConceptActions({
    project,
    concept,
    isArchived,
    onRefetch,
  });

  // Set project data on mount
  useEffect(() => {
    setMobileHeaderData({
      projectId: project.id,
      projectName: project.attributes.name,
    });
  }, [project.id, project.attributes.name, setMobileHeaderData]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Set up inline render settings panel data for xl+ screens
  const [showRenameAngleDialog, setShowRenameAngleDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showEditAnglesModal, setShowEditAnglesModal] = useState(false);
  // Apple-parity "image source" sheet for the secondary-views "+" button.
  // Mounts an AngleUpload(autoOpen) — it owns the MediaSourceSelector flow
  // (project-angles thumbnails, file upload, render picker). Single-sourced
  // through the existing AngleUpload pipeline (DRY).
  const [showAddSourceSheet, setShowAddSourceSheet] = useState(false);
  const [isAttachingProjectAngle, setIsAttachingProjectAngle] = useState(false);

  // Project-angle gallery shown at the top of the MediaSourceSelector.
  // Only angles that finished validation as `valid` are pickable, and we
  // hide angles already attached to this concept so the strip is "what's
  // still attachable".
  const { data: allProjectAngles = [] } = useProjectAngles(project.id);
  const attachedProjectAngleIds = useMemo(
    () =>
      new Set(
        conceptAngles
          .map((ca) => ca.relationships?.angle?.data?.id)
          .filter((id): id is string => !!id)
      ),
    [conceptAngles]
  );
  const sheetProjectAngles = useMemo(
    () =>
      allProjectAngles
        .filter((a) => isProjectAngleValid(a) && isRenderCandidate(a))
        .filter((a) => !attachedProjectAngleIds.has(a.id))
        .map((a) => ({
          id: a.id,
          fileUrl: a.attributes.file_url,
          name:
            a.attributes.name ||
            a.attributes.original_filename ||
            null,
        })),
    [allProjectAngles, attachedProjectAngleIds]
  );

  // Apple-parity attach flow: tap a thumbnail in the sheet OR finish an
  // upload → attach the angle(s) as derivative(s) on the current concept and
  // surface the new one as the active tab via the pendingNewAngleIds
  // plumbing.
  const handleAttachProjectAngles = useCallback(
    async (newAngleIds: string[]) => {
      if (isAttachingProjectAngle || newAngleIds.length === 0) return;
      setIsAttachingProjectAngle(true);
      try {
        const currentIds = conceptAngles
          .map((ca) => ca.relationships?.angle?.data?.id)
          .filter((id): id is string => !!id);
        // Main slot is anchored at index 0; new angles attach as derivatives
        // at the end of the list, mirroring ConceptAnglesEditModal's order
        // semantics on Save. Skip ids already attached so re-uploads don't
        // create duplicate rows.
        const existing = new Set(currentIds);
        const toAttach = newAngleIds.filter((id) => !existing.has(id));
        // Attach-existing path (library thumbnail): the angle exists but isn't
        // on this concept yet, so sync it in as a derivative. The upload / render
        // path (STRICT add-view intake) already attached it server-side by the
        // time we get here, so toAttach is empty and no sync is needed.
        if (toAttach.length > 0) {
          const nextIds = [...currentIds, ...toAttach];
          await conceptsService.syncConceptAngles(
            concept.id,
            nextIds.map((id, index) => ({
              angle_id: id,
              type: index === 0 ? ('main' as const) : ('derivative' as const),
            }))
          );
        }
        // Always surface the just-added view as the active tab (Apple parity:
        // select the new derivative after the mutation). Key on ALL provided
        // ids so this fires whether the angle was server-attached (upload /
        // render pick) or client-synced (library thumbnail) — the resolver maps
        // them to concept_angle ids once the refetch lands.
        setPendingNewAngleIds(newAngleIds);
        await handleAngleUploadSuccess();
      } catch (err) {
        console.error('Failed to attach project angle:', err);
        toast.error('Failed to attach angle');
      } finally {
        setIsAttachingProjectAngle(false);
      }
    },
    [
      isAttachingProjectAngle,
      conceptAngles,
      concept.id,
      handleAngleUploadSuccess,
    ]
  );

  const handleAttachProjectAngle = useCallback(
    (angleId: string) => handleAttachProjectAngles([angleId]),
    [handleAttachProjectAngles]
  );

  const setRenderSettingsDataRef = useRef(desktopLayout?.setRenderSettingsData);
  setRenderSettingsDataRef.current = desktopLayout?.setRenderSettingsData;

  // "+" in the angle tab row (both layouts) opens the image-source sheet —
  // Apple/Android parity. Reordering / detaching stays behind the kebab's
  // "Edit views" entry (ConceptAnglesEditModal).
  const onAddAngle = useCallback(() => {
    setShowAddSourceSheet(true);
  }, []);

  const renderSettingsDataRef = useRef({
    project,
    concept,
    conceptAngles,
    angles,
    renders,
    controls: controlsProp,
    references: referencesProp,
    onRefetch,
  });
  renderSettingsDataRef.current = {
    project,
    concept,
    conceptAngles,
    angles,
    renders,
    controls: controlsProp,
    references: referencesProp,
    onRefetch,
  };

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const setData = setRenderSettingsDataRef.current;
    if (!setData) return;

    if (showInlineRenderPanel && concept && !isArchived) {
      const data = renderSettingsDataRef.current;
      // The two-column inspector reads the active angle from the layout
      // context (`selectedAngleId`) and the carousel render selection from the
      // concept store, so no explicit angle/render id is threaded here.
      setData({
        project: data.project,
        concept: data.concept!,
        conceptAngles: data.conceptAngles,
        angles: data.angles,
        renders: data.renders,
        controls: data.controls,
        references: data.references,
        onRefetch: data.onRefetch,
      });
    } else {
      setData(null);
    }

    return () => {
      if (!isMountedRef.current) {
        setRenderSettingsDataRef.current?.(null);
      }
    };
    // The reactive data is read from renderSettingsDataRef (always fresh), but
    // the effect must RE-PUBLISH into DesktopLayoutContext whenever that data
    // changes — otherwise the two-column inspector keeps the snapshot captured
    // at mount. Missing controls/renders/angles/references here is why a newly
    // created sketch didn't show in the two-column layout until a page refresh.
  }, [
    showInlineRenderPanel,
    concept?.id,
    isArchived,
    controlsProp,
    renders,
    conceptAngles,
    angles,
    referencesProp,
    onRefetch,
  ]);

  // Show toast for errors
  useEffect(() => {
    if (ui.error) {
      toast.error(ui.error);
      const timer = setTimeout(() => {
        setError(null);
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [ui.error, setError]);

  const mainAngle = conceptAngles.find((ca) => ca.attributes.type === 'main');
  const derivativeAngles = conceptAngles.filter(
    (ca) => ca.attributes.type === 'derivative'
  );

  // Resolve pendingNewAngleIds (set after any add-view: upload / render pick /
  // library-thumbnail attach / ConceptAnglesEditModal) to a conceptAngle.id and
  // auto-select that tab, so the user sees confirmation the new derivative
  // landed. Runs after the post-save refetch bubbles into conceptAngles. Layout-
  // agnostic — single-column and two-column both read this selection.
  useEffect(() => {
    if (pendingNewAngleIds.length === 0) return;
    if (!desktopLayout) {
      setPendingNewAngleIds([]);
      return;
    }
    const match = derivativeAngles.find((ca) =>
      pendingNewAngleIds.includes(ca.relationships?.angle?.data?.id ?? '')
    );
    if (match) {
      desktopLayout.setSelectedAngleId(match.id);
      setPendingNewAngleIds([]);
    }
  }, [pendingNewAngleIds, derivativeAngles, desktopLayout]);

  // Layout detection
  const selectedDerivativeAngle = selectedAngleIdFromContext
    ? derivativeAngles.find((ca) => ca.id === selectedAngleIdFromContext)
    : null;

  const isTwoColumnLayout = showInlineRenderPanel;
  // Angle/tab selection is layout-agnostic now — both arrangements show ONE
  // angle at a time (Apple/Android parity). Stacked (single-column) reuses the
  // same context-driven `activeTab` + `selectedAngleId` as two-column.
  const isDesignTab = activeTab === 'design';
  const showMainAngleOnly = isDesignTab && !selectedAngleIdFromContext;
  const showSelectedDerivativeOnly =
    isDesignTab && !!selectedAngleIdFromContext && !!selectedDerivativeAngle;
  const showAlbumListOnly = activeTab === 'album';
  const showEstimateOnly = activeTab === 'estimate';

  // Adaptive title — full ("Design #N. Title") when it fits, compact
  // ("#N Title") otherwise. Mirrors Apple's `ViewThatFits` (iOS) and
  // Android's `BoxWithConstraints + TextMeasurer` in `ConceptDetailTopBar`.
  const fullTitle = concept
    ? concept.attributes.title
      ? tConcept('conceptNumberWithTitle', {
          number: concept.attributes.project_sequence_number,
          title: concept.attributes.title,
        })
      : tConcept('conceptNumber', {
          number: concept.attributes.project_sequence_number,
        })
    : '';
  const compactTitle = concept
    ? concept.attributes.title
      ? `#${concept.attributes.project_sequence_number} ${concept.attributes.title}`
      : `#${concept.attributes.project_sequence_number}`
    : '';
  const titleContainerRef = useRef<HTMLDivElement>(null);
  const titleMeasurerRef = useRef<HTMLSpanElement>(null);
  const [titleShowFull, setTitleShowFull] = useState(true);

  // One-shot "new design created" header-title pulse. Fired only when the page
  // arrived via a creation push (`justCreated` from `?created=1`). The concept
  // page does NOT remount on same-route changes, so we guard with a ref to fire
  // exactly once, drive the CSS class via state (cleared on animationend so it
  // can retrigger if a future creation lands), and strip `?created` from the URL
  // immediately (router.replace, history-safe) so refresh / back never replay it.
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isCreatedPulsing, setIsCreatedPulsing] = useState(false);
  const createdPulseFiredRef = useRef(false);
  useEffect(() => {
    if (!justCreated || createdPulseFiredRef.current) return;
    createdPulseFiredRef.current = true;
    setIsCreatedPulsing(true);
    // Strip the one-shot param without adding a history entry.
    const params = new URLSearchParams(searchParams);
    params.delete('created');
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false,
    });
  }, [justCreated, pathname, searchParams, router]);

  useEffect(() => {
    const container = titleContainerRef.current;
    const measurer = titleMeasurerRef.current;
    if (!container || !measurer) return;
    const check = () => {
      setTitleShowFull(measurer.offsetWidth <= container.clientWidth);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(container);
    return () => observer.disconnect();
  }, [fullTitle]);

  // Page header — Back (left), tap-to-rename title (center),
  // prev/next chevrons + favorite + page-level kebab (right). Mirrors
  // Apple macOS ConceptNavigationToolbar: nav arrows live next to the
  // kebab in the primaryAction slot, with the system back button on the
  // leading side. Favorite stays in the in-content generate row in
  // single-column (matching iPhone) and lives in the header in
  // two-column (matching macOS). Rendered inside the main column in
  // both layouts so it does not stretch over the inline render-settings
  // aside in two-column mode.
  const headerContent = useMemo(() => {
    if (!concept) return null;
    return (
      <div className="flex items-center gap-2">
      <div className="flex items-center min-w-0">
        {/* Tab-aware back. Returns to the project list page matching the
            active tab: Views → designs, Estimate → estimates,
            Blueprint → albums. */}
        <BackButton
          href={
            activeTab === 'estimate'
              ? `/project/${project.id}/estimates`
              : activeTab === 'album'
                ? `/project/${project.id}/albums`
                : `/project/${project.id}/concepts`
          }
          label={tCommon('back')}
        />
        {/* Non-interactive title — the rename-concept action was removed; the
            title is now read-only. Capped via `max-w-xs` so an over-long title
            can't push the Design/Blueprint picker off-centre; overflow
            ellipsizes. */}
        <div
          ref={titleContainerRef}
          className="relative ms-2 min-w-0 max-w-xs"
        >
          {/* Hidden measurer renders the full title to detect whether it fits. */}
          <span
            ref={titleMeasurerRef}
            aria-hidden
            className="invisible absolute left-0 top-0 whitespace-nowrap pointer-events-none text-card-title"
          >
            {fullTitle}
          </span>
          <span
            className={cn(
              'text-card-title text-foreground block w-full whitespace-nowrap overflow-hidden text-ellipsis text-start',
              isCreatedPulsing && 'animate-new-design-pulse'
            )}
            onAnimationEnd={() => setIsCreatedPulsing(false)}
          >
            {titleShowFull ? fullTitle : compactTitle}
          </span>
        </div>
      </div>
      {/* Flexible spacer — pushes the Design/Blueprint picker toward the
          page midline instead of letting it stick to the favorite/menu
          cluster. Symmetric with the spacer on the right. */}
      <div className="flex-1" />
      {/* Design/Blueprint segmented picker — two-column only. Lives between
          two flexible spacers so it floats near the visual centre of the
          bar, separate from the favorite/menu island on the trailing edge. */}
      {isTwoColumnLayout && desktopLayout && (
        <Tabs
          value={desktopLayout.activeTab}
          onValueChange={(v) => {
            // Instant UI update + persist to the URL (single source of truth).
            desktopLayout.setActiveTab(v as MiddlePanelTab);
            onTabChange?.(v as MiddlePanelTab);
          }}
          className="w-[300px]"
        >
          <TabsList className="w-full h-9 bg-settings-card rounded-full p-1">
            <TabsTrigger
              value="design"
              className="flex-1 rounded-full text-muted-foreground data-[state=active]:bg-segment-active data-[state=active]:text-foreground data-[state=active]:shadow-sm focus-visible:ring-0 focus-visible:outline-none"
            >
              {tCommon('tabs.design')}
            </TabsTrigger>
            <TabsTrigger
              value="estimate"
              className="flex-1 rounded-full text-muted-foreground data-[state=active]:bg-segment-active data-[state=active]:text-foreground data-[state=active]:shadow-sm focus-visible:ring-0 focus-visible:outline-none"
            >
              {tCommon('tabs.estimate')}
            </TabsTrigger>
            <TabsTrigger
              value="album"
              className="flex-1 rounded-full text-muted-foreground data-[state=active]:bg-segment-active data-[state=active]:text-foreground data-[state=active]:shadow-sm focus-visible:ring-0 focus-visible:outline-none"
            >
              {tCommon('tabs.blueprint')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {/* Mirror spacer — symmetric to the one above so the picker stays
          centred regardless of left-cluster width. */}
      <div className="flex-1" />
      <div className="flex items-center gap-1">
        <FeedbackHeaderButton />
        {/* Prev/next concept paging — moved into the trailing cluster next to
            the kebab (Apple macOS ConceptNavigationToolbar parity). Hidden
            entirely when the project has a single concept (no prev AND no next):
            the arrows would only ever sit disabled, so they'd be dead chrome. */}
        {(prevConceptId || nextConceptId) && (
          <>
            <Button
              variant="ghost"
              size="icon"
              disabled={!prevConceptId}
              onClick={onSwipeRight}
              aria-label={tConcept('previousConcept')}
              className={cn(
                'relative inline-flex h-11 w-11 items-center justify-center bg-transparent hover:bg-transparent',
                "before:absolute before:inset-2 before:rounded-full before:content-['']",
                'before:bg-transparent hover:before:bg-muted',
                // Override the global disabled grey fill (buttonVariants base) so an
                // un-navigable arrow reads as a plain greyed icon, not a grey pill.
                'disabled:bg-transparent disabled:text-muted-foreground disabled:before:bg-transparent'
              )}
            >
              <ChevronLeft className="relative h-4 w-4 rtl:-scale-x-100" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={!nextConceptId}
              onClick={onSwipeLeft}
              aria-label={tConcept('nextConcept')}
              className={cn(
                'relative inline-flex h-11 w-11 items-center justify-center bg-transparent hover:bg-transparent',
                "before:absolute before:inset-2 before:rounded-full before:content-['']",
                'before:bg-transparent hover:before:bg-muted',
                // Override the global disabled grey fill (buttonVariants base) so an
                // un-navigable arrow reads as a plain greyed icon, not a grey pill.
                'disabled:bg-transparent disabled:text-muted-foreground disabled:before:bg-transparent'
              )}
            >
              <ChevronRight className="relative h-4 w-4 rtl:-scale-x-100" />
            </Button>
          </>
        )}
        {/* Favorite is a dedicated header button only in two-column (macOS
            parity). In single-column it collapses into the kebab's first item
            (see ConceptActions showFavorite) to keep the trailing cluster
            compact (iPhone parity). */}
        {isTwoColumnLayout && (
          <FavoriteButton
            projectId={project.id}
            conceptId={concept.id}
            isFavorite={isConceptFavorite}
            onToggle={toggleConceptFavorite}
          />
        )}
        <ConceptActions
          projectId={project.id}
          conceptId={concept.id}
          conceptName={concept.attributes.title || project.attributes.name || 'Concept'}
          mode="page"
          showFavorite={!isTwoColumnLayout}
          isFavorite={isConceptFavorite}
          onToggleFavorite={toggleConceptFavorite}
          // "Edit views" only while the Views tab is active — the
          // Estimate/Album tabs hide the angle editor entry.
          onEditAngles={
            activeTab === 'design'
              ? () => setShowEditAnglesModal(true)
              : undefined
          }
          onDeleteEstimate={
            deletableEstimateId
              ? () => deleteEstimate(deletableEstimateId)
              : undefined
          }
          isDeletingEstimate={isDeletingEstimate}
          exportableEstimateId={exportableEstimateId}
          className="before:bg-transparent hover:before:bg-muted"
        />
      </div>
    </div>
    );
  }, [
    concept,
    project.id,
    project.attributes.name,
    fullTitle,
    compactTitle,
    titleShowFull,
    isCreatedPulsing,
    isTwoColumnLayout,
    prevConceptId,
    nextConceptId,
    onSwipeRight,
    onSwipeLeft,
    tConcept,
    tCommon,
    desktopLayout,
    activeTab,
    isConceptFavorite,
    toggleConceptFavorite,
    deletableEstimateId,
    exportableEstimateId,
    deleteEstimate,
    isDeletingEstimate,
  ]);

  return (
    <div className={cn(isTwoColumnLayout ? 'space-y-6' : 'space-y-2')}>
      {concept && (
        <>
          {/* Header placement:
              - Two-column (xl+): portal into the layout's full-width
                topbar slot so the bar stretches over content +
                inspector aside (Apple macOS / iPad parity). The
                Design/Blueprint segmented picker lives in the trailing
                cluster of the same bar.
              - Single-column: render inline at the top of the page. */}
          {/* Two-column: header portals into the layout's full-width topbar
              slot, which sits above the scroll area and stays static. */}
          {headerContent &&
            isTwoColumnLayout &&
            desktopLayout?.topBarSlot &&
            createPortal(headerContent, desktopLayout.topBarSlot)}

          {/* Single-column: ONLY the page header (title + chevrons + menu) stays
              pinned at the top. The Design/Blueprint picker and the angle switcher
              both scroll with the content below (Apple/Android parity) — see the
              picker block and the Design block. Solid `bg-panel` (no backdrop-blur)
              so scrolled content never shows through. Mirrors Apple's static
              concept toolbar (nav bar only). */}
          {!isTwoColumnLayout && headerContent && (
            <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 bg-panel">
              <div className="px-4 sm:px-6 lg:px-8 pt-3 pb-2">{headerContent}</div>
            </div>
          )}

          {/* Single-column: Design/Blueprint picker in normal scroll flow, above
              both the Design and Album content so it controls (and scrolls with)
              either tab. Ancestor supplies the horizontal page inset; only the
              vertical rhythm + centering cap live here. */}
          {!isTwoColumnLayout && (
            <div className="py-0">
              <Tabs
                value={activeTab}
                onValueChange={(v) => {
                  // Instant UI update + persist to the URL (single source of truth).
                  desktopLayout?.setActiveTab(v as MiddlePanelTab);
                  onTabChange?.(v as MiddlePanelTab);
                }}
                // Capped + centered on wide screens (max-w-md mx-auto) instead
                // of stretching full width — matches Apple, and identical
                // sizing to the Explore/Refine toggle below (h-10, p-1).
                className="w-full max-w-[560px] mx-auto"
              >
                <TabsList className="w-full h-10 bg-settings-card border border-border rounded-full p-1">
                  <TabsTrigger
                    value="design"
                    className="h-full flex-1 rounded-full text-muted-foreground data-[state=active]:bg-segment-active data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm focus-visible:ring-0 focus-visible:outline-none"
                  >
                    {tCommon('tabs.design')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="estimate"
                    className="h-full flex-1 rounded-full text-muted-foreground data-[state=active]:bg-segment-active data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm focus-visible:ring-0 focus-visible:outline-none"
                  >
                    {tCommon('tabs.estimate')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="album"
                    className="h-full flex-1 rounded-full text-muted-foreground data-[state=active]:bg-segment-active data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm focus-visible:ring-0 focus-visible:outline-none"
                  >
                    {tCommon('tabs.blueprint')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* Two-column: angle tabs at the top of the content column. */}
          {isTwoColumnLayout && (
            <ConceptContentTabs
              conceptAngles={conceptAngles}
              onAddAngle={onAddAngle}
            />
          )}

          {/* Design tab — ONE angle viewer (shared by both arrangements).
              Two-column renders only the viewer here (its render settings live
              in the inspector aside via ProjectDesktopLayout); single-column
              stacks the inline render settings + a floating Generate below. */}
          {/* `pb-36` (here and on the estimate/album tabs below) clears the
              fixed Generate capsule + nav island. Its px value is mirrored by
              BOTTOM_BAR_REVEAL_END_INSET_PX in bottom-bar-reveal.ts so the nav
              reveal anchors to the content's end, not the padded bottom — keep
              them in sync. */}
          {isDesignTab && (
            <div className={cn('space-y-4', !isTwoColumnLayout && 'pb-36')}>
              {/* Single-column (phone): the angle switcher rides at the top of the
                  scroll flow above the viewer, so it scrolls away with the content
                  instead of being pinned in the header (Apple/Android parity).
                  Two-column mounts a pinned switcher above the content column. */}
              {!isTwoColumnLayout && (
                // Align the angle tabs with the content column's horizontal
                // padding (px-4 sm:px-6 lg:px-8). Each tab button already adds
                // its own px-4 (16px), so we only add the breakpoint remainder
                // (0 / 8 / 16px). Edge-to-edge scroll is preserved (overflow-x
                // lives inside ConceptContentTabs).
                <div className="sm:px-2 lg:px-4">
                  <ConceptContentTabs
                    conceptAngles={conceptAngles}
                    onAddAngle={onAddAngle}
                    pinned={false}
                  />
                </div>
              )}
              {showMainAngleOnly && (
                <MainView
                  project={project}
                  concept={concept}
                  mainAngle={mainAngle}
                  conceptAngles={conceptAngles}
                  angles={angles}
                  limits={limits}
                  isMax={isMax}
                  isArchived={isArchived}
                  onEditNotes={() => setShowNotesDialog(true)}
                  onRenameAngle={() => setShowRenameAngleDialog(true)}
                  onEditAngles={() => setShowEditAnglesModal(true)}
                  onAngleUploadSuccess={handleAngleUploadSuccess}
                  onCreateNewConcept={createConceptFromAngle}
                  onDraggingSliderChange={onDraggingSliderChange}
                  onSwipeLeft={onSwipeLeft}
                  onSwipeRight={onSwipeRight}
                  hideRenderButton={true}
                />
              )}

              {showSelectedDerivativeOnly && selectedDerivativeAngle && (
                <DerivativeAnglesView
                  project={project}
                  concept={concept}
                  mainAngle={mainAngle!}
                  conceptAngles={conceptAngles}
                  derivativeAngles={[selectedDerivativeAngle]}
                  angles={angles}
                  limits={limits}
                  isMax={isMax}
                  isArchived={isArchived}
                  onAngleUploadSuccess={handleAngleUploadSuccess}
                  onDeleteAngle={handleDeleteAngle}
                  onDeleteRender={handleDeleteRender}
                  hideRenderButton={true}
                  hideHeader={true}
                />
              )}

              {/* Stacked render settings — single-column only. Flows under the
                  viewer in one page scroll with a floating Generate capsule
                  above the bottom nav. Gated on a real main angle + editable
                  concept so an angle-less / archived concept shows no dangling
                  Generate. Two-column mounts the inline panel in the inspector
                  aside (ProjectDesktopLayout) instead. */}
              {!isTwoColumnLayout && mainAngle && !isArchived && (
                // Cap + center the settings form on wide single-column screens
                // (max-w-md mx-auto) so it doesn't stretch edge-to-edge — the
                // viewer above stays full width. Mirrors the Estimate tab and
                // the Design/Blueprint picker toggle.
                <div className="mx-auto w-full max-w-[560px]">
                  <UnifiedRenderPanel
                    isStacked
                    showAlbumTab={false}
                    footerBottomClassName={stackedFooterBottomClassName}
                    project={project}
                    concept={concept}
                    conceptAngles={conceptAngles}
                    angles={angles}
                    renders={renders}
                    controls={controlsProp}
                    onRefetch={onRefetch}
                  />
                </div>
              )}
            </div>
          )}

          {/* Estimate tab. Two-column fills the content column with the
              estimate document while the settings + Regenerate CTA live in
              the inspector aside (EstimateSettingsPanel, mounted alongside
              renderSettingsData — hence the !isArchived match); single-column
              flows everything in the page scroll with bottom padding clearing
              the floating nav. */}
          {showEstimateOnly &&
            (isTwoColumnLayout ? (
              <div className="h-full">
                <EstimateTabContent
                  projectId={project.id}
                  conceptId={concept.id}
                  settingsHostedExternally={!isArchived}
                />
              </div>
            ) : (
              // NOT capped here: the Estimate tab caps its parts individually —
              // the generated estimate DOCUMENT (totals + materials/labor table)
              // stays full width while the intro/draft and the inline settings
              // list are capped inside EstimateTabContent / EstimateEditor.
              <div className="pb-36">
                <EstimateTabContent
                  projectId={project.id}
                  conceptId={concept.id}
                  isStacked
                />
              </div>
            ))}

          {/* Album tab.
              - Two-column: albums list + viewer (settings live in the aside).
              - Single-column: albums list + inline album settings + a floating
                Generate capsule (AlbumCollapsiblePanel, no drawer). */}
          {showAlbumListOnly &&
            (isTwoColumnLayout ? (
              <div className="flex h-full flex-col">
                <div className="min-h-0 flex-1">
                  <AlbumListWithViewer
                    conceptId={concept.id}
                    projectId={project.id}
                    conceptTitle={concept.attributes.title || null}
                    conceptNumber={concept.attributes.project_sequence_number}
                    coverImageUrl={concept.attributes.main_render_url || null}
                  />
                </div>
              </div>
            ) : (
              !isArchived && (
                <div className="pb-36 scroll-mt-6" ref={albumSectionRef}>
                  {/* Cap + center the settings form on wide single-column
                      screens (max-w-md mx-auto), matching the Views/Estimate
                      tabs — the form shouldn't stretch edge-to-edge. */}
                  <div className="mx-auto w-full max-w-[560px]">
                    <AlbumCollapsiblePanel
                      conceptId={concept.id}
                      conceptAngles={conceptAngles}
                      isArchived={isArchived}
                      projectId={project.id}
                      conceptTitle={concept.attributes.title || null}
                      conceptNumber={concept.attributes.project_sequence_number}
                      coverImageUrl={concept.attributes.main_render_url || null}
                      footerBottomClassName={stackedFooterBottomClassName}
                    />
                  </div>
                </div>
              )
            ))}
        </>
      )}

      {concept && (
        <>
          <RenameAngleDialog
            angleId={mainAngle?.relationships?.angle?.data?.id ?? null}
            currentName={
              mainAngle?.relationships?.angle?.meta?.name ?? null
            }
            open={showRenameAngleDialog}
            onOpenChange={setShowRenameAngleDialog}
            onSave={async (angleId, newName) => {
              await anglesService.updateAngle(angleId, {
                name: newName.length > 0 ? newName : null,
              });
              await onRefetch();
            }}
          />

          <ConceptNotesDialog
            open={showNotesDialog}
            onOpenChange={setShowNotesDialog}
            note={concept.attributes.note || ''}
            onSave={handleNoteUpdate}
          />

          <ConceptAnglesEditModal
            mode="edit"
            projectId={project.id}
            conceptId={concept.id}
            conceptAngles={conceptAngles}
            open={showEditAnglesModal}
            onOpenChange={setShowEditAnglesModal}
            onComplete={async (newlyAttachedAngleIds) => {
              // Defer the tab switch: conceptAngles is still the pre-refetch
              // snapshot here. A useEffect watches conceptAngles and resolves
              // the pending angle IDs to conceptAngle IDs once the refetch
              // lands, then calls setSelectedAngleId.
              setPendingNewAngleIds(newlyAttachedAngleIds);
              await handleAngleUploadSuccess();
            }}
          />

          {/* Cross-concept view-delete conflict (409): the deleted view has
              renders in — or is the main of — other concepts. The user picks a
              strategy and we retry (phase 2). Mirrors the Brief-library delete
              and the Apple/Android conflict sheets. */}
          <AngleDeleteConflictDialog
            conflict={deleteConflict}
            detail={deleteConflictDetail}
            isPending={isDeletingAngle}
            onChoose={handleDeleteConflictChoose}
            onCancel={dismissDeleteConflict}
          />

          {/* Image-source picker — Apple imageSource sheet parity.
              Mounts AngleUpload with autoOpen=true so MediaSourceSelector
              fires immediately; on completion it auto-attaches the new
              angle as a derivative on this concept. Project-angle thumbnails
              are surfaced inside the selector for instant attach (no upload). */}
          {showAddSourceSheet && (
            <div className="hidden">
              <AngleUpload
                conceptId={concept.id}
                allowMultiple
                currentAngleCount={conceptAngles.length}
                currentProjectAngleLibraryCount={allProjectAngles.length}
                mediaSourceProjectAngles={sheetProjectAngles}
                onMediaSourceProjectAngleSelected={(angleId) => {
                  setShowAddSourceSheet(false);
                  void handleAttachProjectAngle(angleId);
                }}
                onSuccess={async (response) => {
                  setShowAddSourceSheet(false);
                  const responses = Array.isArray(response)
                    ? response
                    : [response];
                  const newAngleIds = responses
                    .map((r) => r.id)
                    .filter((id): id is string => !!id);
                  if (newAngleIds.length === 0) {
                    await handleAngleUploadSuccess();
                    return;
                  }
                  // Auto-attach freshly uploaded angle(s) as derivatives.
                  await handleAttachProjectAngles(newAngleIds);
                }}
                onCancel={() => setShowAddSourceSheet(false)}
                onError={() => setShowAddSourceSheet(false)}
                autoOpen
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
